import "../../styles/insertAndTestSecurityKey.scss";

import { Col, Hr } from "@amzn/stencil-react-components/layout";
import { Link } from "@amzn/stencil-react-components/link";
import {
  MessageBanner,
  MessageBannerType,
} from "@amzn/stencil-react-components/message-banner";
import { Text } from "@amzn/stencil-react-components/text";
import { FormattedMessage } from "react-intl";

import { StepBodyStyledText } from "@/components/Commons/StepBodyStyledText";

import SecurityKeyActionButtons from "./SecurityKeyActionButtons";
import SecurityKeyEducationalContent from "./SecurityKeyEducationalContent";

/**
 * Props for the SecurityKeyInstructions component
 */
interface SecurityKeyInstructionsProps {
  /** Callback function triggered when user indicates they have inserted a security key */
  onDetectionStart: () => void;
  /** Whether multiple security keys have been detected */
  showMultipleKeysError?: boolean;
  /** Callback function for continuing without testing (used in multiple keys error) */
  onContinueWithoutTesting: () => void;
}

/**
 * Initial instruction screen component for security key testing workflow.
 *
 * This component displays educational content about security keys and provides
 * the user with instructions and action buttons to begin the security key
 * detection process. It also handles error states when multiple security keys
 * are detected simultaneously.
 *
 * @param props - The component props
 * @param props.onDetectionStart - Function called when user indicates they have inserted a security key
 * @param props.showMultipleKeysError - Whether to display error message for multiple keys detected
 * @param props.onContinueWithoutTesting - Function called when user chooses to continue without testing (multiple keys error)
 * @returns JSX element containing the instruction screen with educational content and action buttons
 */
export default function SecurityKeyInstructions({
  onDetectionStart,
  showMultipleKeysError = false,
  onContinueWithoutTesting,
}: SecurityKeyInstructionsProps): JSX.Element {
  return (
    <Col rowGap="S400">
      <StepBodyStyledText>
        <Col rowGap="S300">
          <SecurityKeyEducationalContent />

          <Hr color="neutral20" size="narrow" />

          <Text
            fontSize="T300"
            fontWeight="bold"
            dataTestId="testSecurityKeyHeading"
          >
            <FormattedMessage id="insertAndTestSecurityKey-instructions-testSecurityKeyHeading" />
          </Text>

          <Text fontWeight="regular" dataTestId="testSecurityKeyInstruction">
            <FormattedMessage
              id="insertAndTestSecurityKey-instructions-testSecurityKeyInstruction"
              values={{
                strong: (chunks: React.ReactNode) => <strong>{chunks}</strong>,
              }}
            />
          </Text>
        </Col>
      </StepBodyStyledText>

      {showMultipleKeysError && (
        <MessageBanner type={MessageBannerType.Error}>
          <Text>
            <FormattedMessage id="insertAndTestSecurityKey-instructions-multipleKeysError" />
            <Link
              onClick={() => {
                onContinueWithoutTesting();
              }}
              className="continue-link"
            >
              <strong>
                <FormattedMessage id="insertAndTestSecurityKey-instructions-continueLink" />
              </strong>
            </Link>
          </Text>
        </MessageBanner>
      )}

      <SecurityKeyActionButtons
        onPrimaryAction={onDetectionStart}
        hideNoSecurityKeyButton={showMultipleKeysError}
      />
    </Col>
  );
}
